import CampaignPlaceholder from "@/assets/images/campaign-placeholder.webp";
import { Box } from "@repo/components/src/ui/box";
import { Image } from "@repo/components/src/ui/image";
import { Pressable } from "@repo/components/src/ui/pressable";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState } from "react";

type Props = {
  imageUrls: string[];
};

/** Same scrim as `apps/mobil/.../HeroImage.tsx` — a dark bottom gradient so a
 * white pagination dot or a light image never disappears into the card edge. */
const Scrim = () => (
  <LinearGradient
    colors={["rgba(0,0,0,0)", "rgba(0,0,0,0.25)"]}
    locations={[0.45, 1]}
    pointerEvents="none"
    style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
  />
);

/**
 * The draft's cover, exactly as a creator would see it atop the campaign in the
 * mobile app — an `aspect-video` image with a gradient scrim, plus dot paging
 * when the draft has more than one media.
 *
 * Deliberately NOT `react-native-reanimated-carousel`: verified live that it
 * mounts nothing on web inside this panel (its web support is unreliable), so
 * the pager is plain state — tap a dot, swap the image. Same visual result,
 * zero exotic dependencies.
 *
 * Never an empty gray box: a draft with no media yet is a normal, expected state
 * — so this shows the designed "Aún no hay imagen de campaña" placeholder
 * instead of anything that reads as broken.
 */
const Hero = React.memo(({ imageUrls }: Props) => {
  const [active, setActive] = useState(0);
  const activeUrl = imageUrls[Math.min(active, imageUrls.length - 1)];

  if (imageUrls.length === 0) {
    return (
      <Box className="w-full aspect-video overflow-hidden rounded-3xl bg-background-950">
        <Image
          source={CampaignPlaceholder}
          alt="Aún no hay imagen de campaña"
          size="full"
          contentFit="cover"
        />
      </Box>
    );
  }

  return (
    <Box className="w-full aspect-video overflow-hidden rounded-3xl bg-background-950">
      <Image
        key={activeUrl}
        source={{ uri: activeUrl }}
        size="full"
        alt="Portada de la campaña"
        contentFit="cover"
        className="animate-fade-in-soft motion-reduce:animate-none"
      />
      <Scrim />
      {imageUrls.length > 1 ? (
        <Box className="absolute bottom-3 w-full flex-row justify-center">
          <Box className="flex-row items-center gap-2 rounded-xl bg-background-900 px-4 py-1">
            {imageUrls.map((url, index) => (
              <Pressable
                key={`${url}-${index}`}
                onPress={() => setActive(index)}
                accessibilityLabel={`Imagen ${index + 1}`}
              >
                <Box
                  className={`h-2 rounded-full ${
                    index === active
                      ? "w-5 bg-background-0"
                      : "w-2 bg-background-300"
                  }`}
                />
              </Pressable>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
});

Hero.displayName = "Hero";

export default Hero;
